import { Accordion, Separator, Text } from '@shared/components';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ConversationTranscript } from '@shared/features/conversation-transcript';
import {
  PricingForm,
  PricingFormValues,
} from '@shared/features/loadboard-details/PricingForm';
import { MovementLoadboardRates } from '@shared/features/movement-loadboard-rates/MovementLoadboardRates';
import { IntegrationType } from '@shared/generated/graphql';
import { useGetMovementConversations } from '@shared/graphql/hooks/movements/useGetMovementConversations';
import useMovement from '@shared/graphql/hooks/movements/useMovement';
import { useFeatureFlagEnabled } from '@shared/hooks/useFeatureFlagEnabled';
import { formatTime } from '@shared/plugin/utils/datetime';
import { IntegrationConfig } from '@shared/types/order';
import { getConfiguredIntegrations } from '@shared/utils/userFeatures/getConfiguredIntegrations';
import { FeatureFlagName } from 'clerk_common/types/featureFlags';
import { useState } from 'react';
import { CarrierOffers } from '../../carrier-offers/CarrierOffers';
import { LoadboardDetails } from '../../loadboard-details/LoadboardDetails';
import { SuggestedCarriers } from '../../suggested-carriers/SuggestedCarriers';
import { ConversationTypeIcon } from './ConversationTypeIcon';
import { MovementDetailsDisplay } from './MovementDetailsDisplay';
import { NegotiationStrategyOverride } from './NegotiationStrategyOverride';

type VoomaCoverProps = {
  // TODO(mike): Remove ShipmentId, use MovementId instead.
  shipmentId: string;
  movementId: string;
};
export function VoomaCover({ shipmentId, movementId }: VoomaCoverProps) {
  const { me, defaultOrgId } = useMeContext();

  const configuredIntegrations = getConfiguredIntegrations(
    me,
    defaultOrgId ?? ''
  );

  const showLoadboardDetails =
    countLoadBoardIntegrations(configuredIntegrations) > 0;
  const showSuggestedCarriers = true;

  if (!showLoadboardDetails && !showSuggestedCarriers) return null;
  const showMovementDetailsDisplay = true;
  const showMovementConversations = true;

  const trigger = (
    <Text isHeavy type="body-sm">
      Cover
    </Text>
  );

  const children = (
    <CoverContents
      shipmentId={shipmentId}
      movementId={movementId}
      showMovementDetailsDisplay={showMovementDetailsDisplay}
      showLoadboardDetails={showLoadboardDetails}
      showSuggestedCarriers={showSuggestedCarriers}
      showMovementConversations={showMovementConversations}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <Separator />
      <Accordion
        sections={[{ trigger, children, startOpen: true }]}
        triggerClassName="h-[36px]"
      />
    </div>
  );
}

type CoverContentsProps = {
  showMovementConversations: boolean;
  shipmentId: string;
  movementId: string;
  showMovementDetailsDisplay: boolean;
  showLoadboardDetails: boolean;
  showSuggestedCarriers: boolean;
};
export function CoverContents({
  shipmentId,
  movementId,
  showMovementDetailsDisplay,
  showMovementConversations,
  showLoadboardDetails,
  showSuggestedCarriers,
}: CoverContentsProps) {
  const { defaultOrgId } = useMeContext();
  const { movement, updateMovement, isLoading, updateIsLoading } = useMovement({
    id: movementId,
  });
  const { isEnabled: showNegotiationStrategyOverride } = useFeatureFlagEnabled({
    name: FeatureFlagName.SHOW_NEGOTIATION_STRATEGY_OVERRIDE,
    organizationId: defaultOrgId,
  });
  const pricingData = movement?.pricing;

  const updatePricing = (data: PricingFormValues) => {
    updateMovement({
      id: movementId,
      pricing: {
        targetPrice: data.targetPrice
          ? parseFloat(data.targetPrice)
          : undefined,
        maxPrice: data.maxPrice ? parseFloat(data.maxPrice) : undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-6 px-1">
      {showMovementDetailsDisplay && (
        <MovementDetailsDisplay
          movementId={movementId}
          commodity={movement?.commodity}
        />
      )}
      <PricingForm
        isLoading={isLoading || updateIsLoading}
        onSubmit={updatePricing}
        recordId={movementId}
        initialFormData={{
          targetPrice: pricingData?.targetPrice?.toString(),
          maxPrice: pricingData?.maxPrice?.toString(),
        }}
      />
      {showMovementConversations && (
        <>
          <Text isHeavy type="body-sm">
            Conversations
          </Text>
          <MovementConversations movementId={movementId} />
        </>
      )}
      {showNegotiationStrategyOverride && (
        <NegotiationStrategyOverride
          movementId={movementId}
          currentOverride={movement?.negotiationStrategyConfigOverride}
          updateMovement={updateMovement}
          isLoading={isLoading || updateIsLoading}
        />
      )}
      <CarrierOffers movementId={movementId} carrierOfferActions={[]} />
      <MovementLoadboardRates movementId={movementId} />
      {showLoadboardDetails && <LoadboardDetails shipmentId={shipmentId} />}
      {showSuggestedCarriers && <SuggestedCarriers shipmentId={shipmentId} />}
    </div>
  );
}

export function countLoadBoardIntegrations(
  configuredIntegrations: Record<IntegrationType, IntegrationConfig>
) {
  const loadBoardIntegrationTypes = [
    IntegrationType.DAT,
    IntegrationType.HOSTED_MCLEOD,
  ];
  return Object.values(configuredIntegrations).filter(
    (integration) =>
      integration.integrationType &&
      loadBoardIntegrationTypes.includes(integration.integrationType)
  ).length;
}

export function countSuggestedCarriersIntegrations(
  configuredIntegrations: Record<IntegrationType, IntegrationConfig>
) {
  const suggestedCarriersIntegrationTypes = [IntegrationType.GENLOGS];
  return Object.values(configuredIntegrations).filter(
    (integration) =>
      integration.integrationType &&
      suggestedCarriersIntegrationTypes.includes(integration.integrationType)
  ).length;
}

function MovementConversations({ movementId }: { movementId: string }) {
  const { conversations, isLoading } = useGetMovementConversations({
    movementId,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (isLoading) return null;
  if (conversations.length === 0) {
    return (
      <Text type="body-sm" className="px-1 py-2 text-gray-500">
        No conversations for this movement.
      </Text>
    );
  }
  return (
    <div className="flex flex-col">
      {conversations.map((conversation) => {
        const isExpanded = expandedId === conversation.id;
        return (
          <div key={conversation.id} className="rounded px-1 py-2">
            <button
              type="button"
              className="w-full text-left hover:bg-gray-50"
              onClick={() => setExpandedId(isExpanded ? null : conversation.id)}
              aria-expanded={isExpanded}
              aria-controls={`conversation-${conversation.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ConversationTypeIcon type={conversation.type} />
                  <Text type="body-sm">
                    {conversation.associatedCarriers[0]?.name ?? '—'}
                  </Text>
                </div>
                <Text type="body-xs" className="shrink-0 text-gray-500">
                  {formatTime(conversation.createdAt)}
                </Text>
              </div>
            </button>
            {isExpanded && (
              <div className="mt-2 border-t pt-2">
                <ConversationTranscript
                  conversation={conversation}
                  actionExecutionEvents={[]}
                  aiSide="right"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
